import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import projectService from '../editor/Configure/services/projectService';
import SaveProjectModal from '../editor/Configure/components/SaveProjectModal';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileSheetWrapper from '../editor/Configure/mobile/MobileSheetWrapper';
import { AccountDetailsPanel } from '../../../components/UserProfilePopover';

const STORAGE_KEY  = 'vastu_projects';
const PLACEHOLDER  = 'https://www.wikihow.com/images/thumb/9/99/Draw-a-Floor-Plan-to-Scale-Step-13-Version-3.jpg/aid191318-v4-1200px-Draw-a-Floor-Plan-to-Scale-Step-13-Version-3.jpg';
const PER_PAGE     = 12;

const readLocal  = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; } };
const writeLocal = (p) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch {} };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); }
  catch { return '—'; }
};

const getInitials = (name = '') =>
  name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'VP';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile: this whole page renders through a separate, image-forward
  // "Library" style grid (see the mobile JSX branch in the return below) —
  // per explicit request, modeled directly on a reference photo-library app
  // screenshot. Desktop keeps its existing table-ish card grid byte-for-byte
  // unchanged; both branches share all the state/handlers above, only the
  // markup differs (same "duplication over abstraction" approach used
  // throughout this codebase's other mobile work).
  const isMobile = useIsMobile();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);

  const [projects,         setProjects]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [search,           setSearch]           = useState('');
  const [searchInput,      setSearchInput]      = useState('');  // debounced
  const [confirmDeleteId,  setConfirmDeleteId]  = useState(null);
  const [showCreateModal,  setShowCreateModal]  = useState(false);
  const [opening,          setOpening]          = useState(false);
  const [justCreated,      setJustCreated]      = useState(location.state?.justCreated ?? null);

  // ── Pagination state ────────────────────────────────────────────────
  const [currentPage,  setCurrentPage]  = useState(1);
  const [lastPage,     setLastPage]     = useState(1);
  const [total,        setTotal]        = useState(0);
  const [metaFrom,     setMetaFrom]     = useState(0);
  const [metaTo,       setMetaTo]       = useState(0);

  // Debounce search input (500ms)
  const debounceRef = useRef(null);
  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setCurrentPage(1); // reset to page 1 on new search
    }, 500);
  };

  // ── Fetch ────────────────────────────────────────────────────────────
  const fetchProjects = useCallback(async (page = 1, searchTerm = '') => {
    setLoading(true); setError(null);
    try {
      const res = await projectService.list({ page, perPage: PER_PAGE, search: searchTerm });
      // Paginated response: { data: [...], meta: { currentPage, lastPage, total, from, to } }
      if (res?.meta) {
        setProjects(res.data ?? []);
        setCurrentPage(res.meta.currentPage);
        setLastPage(res.meta.lastPage);
        setTotal(res.meta.total);
        setMetaFrom(res.meta.from ?? 0);
        setMetaTo(res.meta.to ?? 0);
      } else {
        // Fallback: old non-paginated response or localStorage
        const list = Array.isArray(res) ? res : (res?.data ?? readLocal());
        setProjects(list);
        setLastPage(1); setTotal(list.length); setMetaFrom(1); setMetaTo(list.length);
      }
    } catch {
      const local = readLocal();
      setProjects(local);
      setLastPage(1); setTotal(local.length); setMetaFrom(1); setMetaTo(local.length);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever page or debounced search changes
  useEffect(() => { fetchProjects(currentPage, search); }, [fetchProjects, currentPage, search]);

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpen   = (id) => { setOpening(true); setTimeout(() => navigate(`/editor/${id}`), 30); };
  const handleDelete = async (id) => {
    try {
      try { await projectService.delete(id); } catch { writeLocal(readLocal().filter(p => p.id !== id)); }
      // If last item on page > 1, go back a page
      if (projects.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        fetchProjects(currentPage, search);
      }
      setConfirmDeleteId(null);
    } catch (e) { setError(e.message || 'Failed to delete project'); }
  };
  const handleCreateSubmit = ({ name, description, clientDetails }) => {
    setShowCreateModal(false);
    navigate('/editor/new', { state: { newProjectName: name, newProjectDescription: description, newProjectClientDetails: clientDetails } });
  };

  // ── Page number range to display (max 5 buttons) ────────────────────
  const pageButtons = () => {
    if (lastPage <= 1) return [];
    const delta = 2;
    const range = [];
    const left  = Math.max(2, currentPage - delta);
    const right = Math.min(lastPage - 1, currentPage + delta);
    range.push(1);
    if (left > 2) range.push('...');
    for (let i = left; i <= right; i++) range.push(i);
    if (right < lastPage - 1) range.push('...');
    if (lastPage > 1) range.push(lastPage);
    return range;
  };

  /* ── Opening overlay ─────────────────────────────────────────── */
  if (opening) return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(255,255,255,0.96)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:20, fontFamily:FF, backdropFilter:'blur(4px)' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width:44,height:44,borderRadius:'50%',border:'3px solid #e2e8f0',borderTopColor:'#00A19D',animation:'spin 0.75s linear infinite' }} />
      <span style={{ fontSize:14,color:'#64748b',fontWeight:500 }}>Opening project…</span>
    </div>
  );

  return (
    <div style={isMobile ? { ...ms.page, paddingBottom: lastPage > 1 ? 90 : 32 } : s.page}>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.45}}
        .sk{animation:pulse 1.5s ease-in-out infinite;background:#e9ecef;border-radius:5px}
        .pc:hover{box-shadow:0 6px 24px rgba(0,0,0,0.09)!important;transform:translateY(-2px)!important;border-color:#c8cdd6!important}
        .ob:hover{background:#00857E!important}
        .db:hover{background:#fef2f2!important;color:#ef4444!important;border-color:#fecaca!important}
        .nc:hover{border-color:#00A19D!important}
        .nc:hover .np{color:#00A19D!important}
        .si:focus{border-color:#00A19D!important;outline:none}
        .pg-btn:hover:not(:disabled){background:#00A19D!important;color:#fff!important;border-color:#00A19D!important}
      `}</style>

      {/* ── Project Created Banner ──────────────────────────────── */}
      {justCreated && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', margin: isMobile ? '0 16px 16px' : '0 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>✓</span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#166534' }}>
              Project "<strong>{justCreated}</strong>" created! Ab list mein select karke open karo.
            </span>
          </div>
          <button onClick={() => setJustCreated(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '16px', padding: '0 4px' }}>✕</button>
        </div>
      )}

      {isMobile ? (
        <>
          {/* ── Mobile "Library" header — small caps brand line + big
              title, search toggled via the icon button (matches the
              reference app's minimal top bar instead of a full search
              input always on screen). ── */}
          <div style={ms.header}>
            <div>
              <div style={ms.brand}>Vastu Analyzer</div>
              <h1 style={ms.title}>My Projects</h1>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => fetchProjects(currentPage, search)} style={ms.iconBtn} title="Refresh">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button onClick={() => setMobileSearchOpen(o => !o)} style={ms.iconBtn} title="Search">
                <i className="bi bi-search" />
              </button>
              <button onClick={() => setShowCreateModal(true)} style={ms.iconBtnPrimary} title="New Project">
                <i className="bi bi-plus-lg" />
              </button>
              {/* Account settings — replaces the global top navbar's
                  profile chip (hidden on this page at phone widths, see
                  VastuLayout) with a bottom-sheet, same pattern as every
                  other mobile sheet in the editor, instead of a floating
                  popover. */}
              <button onClick={() => setMobileAccountOpen(true)} style={ms.iconBtn} title="Account">
                <i className="bi bi-gear" />
              </button>
            </div>
          </div>

          {mobileSearchOpen && (
            <div style={{ padding: '0 16px 12px' }}>
              <div style={{ position:'relative' }}>
                <i className="bi bi-search" style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#adb5bd',fontSize:13,pointerEvents:'none' }} />
                <input
                  className="si" type="text" placeholder="Search projects…" autoFocus
                  value={searchInput} onChange={e => handleSearchChange(e.target.value)}
                  style={s.searchInput}
                />
              </div>
            </div>
          )}

          {!loading && !error && (
            <div style={{ padding: '0 16px 10px', fontSize: 11, color: '#94A3AF', fontWeight: 500 }}>
              {total} project{total !== 1 ? 's' : ''}
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div style={{ margin:'0 16px 16px', padding:'12px 14px',background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca',borderRadius:10,fontSize:13,display:'flex',alignItems:'center',gap:8 }}>
              <i className="bi bi-exclamation-triangle-fill" />{error}
            </div>
          )}

          {/* ── Loading skeletons ── */}
          {loading && (
            <div style={ms.grid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <div className="sk" style={{ height:150, borderRadius:16 }} />
                  <div className="sk" style={{ height:12, width:'70%' }} />
                  <div className="sk" style={{ height:10, width:'40%' }} />
                </div>
              ))}
            </div>
          )}

          {/* ── Empty state ── */}
          {!loading && !error && projects.length === 0 && (
            <div style={{ margin:'8px 16px', padding:'40px 24px',background:'#fff',borderRadius:16,border:'1px dashed #dee2e6',textAlign:'center' }}>
              <div style={{ width:52,height:52,background:'#f8f9fa',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,color:'#adb5bd',margin:'0 auto 14px' }}>
                <i className="bi bi-folder2-open" />
              </div>
              <h3 style={{ fontSize:15,fontWeight:700,color:'#212529',margin:'0 0 6px' }}>
                {search ? `No results for "${search}"` : 'No projects yet'}
              </h3>
              <p style={{ fontSize:12,color:'#6c757d',margin:'0 0 18px' }}>
                {search ? 'Try a different keyword.' : 'Create your first project to get started.'}
              </p>
              {!search && (
                <button onClick={() => setShowCreateModal(true)} style={s.primaryBtn}>
                  <i className="bi bi-plus-lg" style={{ marginRight:7 }} />Create Project
                </button>
              )}
            </div>
          )}

          {/* ── Grid ── */}
          {!loading && projects.length > 0 && (
            <>
              <div style={ms.grid}>
                {currentPage === 1 && (
                  <div onClick={() => setShowCreateModal(true)} style={ms.newTile}>
                    <div style={ms.newTileIcon}><i className="bi bi-plus-lg" /></div>
                    <span style={{ fontSize:12,fontWeight:600,color:'#475569' }}>New Project</span>
                  </div>
                )}

                {projects.map(p => {
                  const isConfirming = confirmDeleteId === p.id;
                  const thumb        = p.floorPlanRef || PLACEHOLDER;
                  return (
                    <div key={p.id} style={ms.card}>
                      <div style={ms.thumbBox} onClick={() => !isConfirming && handleOpen(p.id)}>
                        <img src={thumb} alt="Floor plan" style={ms.thumbImg}
                          onError={e => { e.target.src = PLACEHOLDER; }} />
                        <span style={ms.badge}>Saved</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id); }}
                          style={ms.deleteDot} title="Delete"
                        >
                          <i className="bi bi-trash3" />
                        </button>

                        {isConfirming && (
                          <div onClick={(e) => e.stopPropagation()} style={ms.confirmOverlay}>
                            <span style={{ color:'#fff', fontSize:11, fontWeight:600, textAlign:'center', padding:'0 6px' }}>
                              Delete this project?
                            </span>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={() => handleDelete(p.id)} style={ms.confirmYes}>Delete</button>
                              <button onClick={() => setConfirmDeleteId(null)} style={ms.confirmNo}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div onClick={() => !isConfirming && handleOpen(p.id)} style={{ padding:'10px 12px 12px', cursor: isConfirming ? 'default' : 'pointer' }}>
                        <div style={ms.cardName}>{p.name || 'Untitled'}</div>
                        <div style={ms.cardMeta}>{fmtDate(p.updatedAt)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Pagination — floating bottom navbar, same pill style as
                  the editor's MobileBottomToolbar, instead of an inline bar
                  in the page flow. ── */}
              {lastPage > 1 && (
                <div style={ms.pagFloatWrap}>
                  <div style={ms.pagination}>
                    <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} style={ms.pgBtn(currentPage === 1)}>
                      <i className="bi bi-chevron-left" />
                    </button>
                    <span style={ms.pgInfo}>Page {currentPage} of {lastPage}</span>
                    <button disabled={currentPage === lastPage} onClick={() => handlePageChange(currentPage + 1)} style={ms.pgBtn(currentPage === lastPage)}>
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={s.header}>
            <div>
              <h1 style={s.title}>My Projects</h1>
              <p style={s.sub}>Open a saved floor plan or start a fresh analysis</p>
            </div>
            <button onClick={() => setShowCreateModal(true)} style={s.primaryBtn}>
              <i className="bi bi-plus-lg" style={{ marginRight:7 }} />New Project
            </button>
          </div>

          {/* ── Toolbar ──────────────────────────────────────────────── */}
          <div style={s.toolbar}>
            <div style={{ position:'relative', flex:1, maxWidth:380 }}>
              <i className="bi bi-search" style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#adb5bd',fontSize:13,pointerEvents:'none' }} />
              <input
                className="si" type="text" placeholder="Search projects…"
                value={searchInput} onChange={e => handleSearchChange(e.target.value)}
                style={s.searchInput}
              />
              {searchInput && (
                <button onClick={() => handleSearchChange('')} style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'#adb5bd',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center' }}>
                  <i className="bi bi-x" />
                </button>
              )}
            </div>
            <button onClick={() => fetchProjects(currentPage, search)} style={s.ghostBtn} title="Refresh">
              <i className="bi bi-arrow-clockwise" style={{ marginRight:5 }} />Refresh
            </button>
            {!loading && (
              <span style={{ fontSize:12,color:'#adb5bd',fontWeight:500,marginLeft:4,whiteSpace:'nowrap' }}>
                {total} project{total !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* ── Error ────────────────────────────────────────────────── */}
          {!loading && error && (
            <div style={{ padding:'12px 16px',background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca',borderRadius:10,fontSize:14,marginBottom:20,display:'flex',alignItems:'center',gap:8 }}>
              <i className="bi bi-exclamation-triangle-fill" />{error}
            </div>
          )}

          {/* ── Loading skeletons ─────────────────────────────────────── */}
          {loading && (
            <div style={s.grid}>
              {Array.from({ length: PER_PAGE }).map((_, i) => (
                <div key={i} style={{ background:'#fff',borderRadius:14,border:'1px solid #e9ecef',overflow:'hidden' }}>
                  <div className="sk" style={{ height:160 }} />
                  <div style={{ padding:'14px 16px 16px',display:'flex',flexDirection:'column',gap:10 }}>
                    <div className="sk" style={{ height:14,width:'65%' }} />
                    <div className="sk" style={{ height:11,width:'45%' }} />
                    <div style={{ display:'flex',gap:8,marginTop:6 }}>
                      <div className="sk" style={{ flex:1,height:34,borderRadius:8 }} />
                      <div className="sk" style={{ width:36,height:34,borderRadius:8 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Empty state ───────────────────────────────────────────── */}
          {!loading && !error && projects.length === 0 && (
            <div style={{ padding:'48px 32px',background:'#fff',borderRadius:16,border:'1px dashed #dee2e6' }}>
              <div style={{ width:56,height:56,background:'#f8f9fa',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,color:'#adb5bd',marginBottom:16 }}>
                <i className="bi bi-folder2-open" />
              </div>
              <h3 style={{ fontSize:17,fontWeight:700,color:'#212529',margin:'0 0 8px' }}>
                {search ? `No results for "${search}"` : 'No projects yet'}
              </h3>
              <p style={{ fontSize:13,color:'#6c757d',margin:'0 0 22px' }}>
                {search ? 'Try a different keyword.' : 'Create your first project to get started.'}
              </p>
              {!search && (
                <button onClick={() => setShowCreateModal(true)} style={s.primaryBtn}>
                  <i className="bi bi-plus-lg" style={{ marginRight:7 }} />Create Project
                </button>
              )}
            </div>
          )}

          {/* ── Grid ─────────────────────────────────────────────────── */}
          {!loading && projects.length > 0 && (
            <>
              <div style={s.grid}>
                {/* + New Project card — always first on page 1 */}
                {currentPage === 1 && (
                  <div className="nc" style={s.newCard} onClick={() => setShowCreateModal(true)}>
                    <div style={{ width:48,height:48,borderRadius:12,background:'#f8f9fa',border:'1.5px dashed #ced4da',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,marginBottom:12,transition:'border-color 0.15s' }}>
                      <i className="np bi bi-plus-lg" style={{ color:'#adb5bd',transition:'color 0.15s' }} />
                    </div>
                    <span style={{ fontSize:14,fontWeight:600,color:'#495057' }}>New Project</span>
                    <span style={{ fontSize:12,color:'#adb5bd',marginTop:4 }}>Start a fresh analysis</span>
                  </div>
                )}

                {projects.map(p => {
                  const isConfirming = confirmDeleteId === p.id;
                  const thumb        = p.floorPlanRef || PLACEHOLDER;
                  return (
                    <div key={p.id} className="pc" style={s.card}>
                      {/* Thumbnail */}
                      <div style={s.thumb} onClick={() => !isConfirming && handleOpen(p.id)}>
                        <img src={thumb} alt="Floor plan" style={s.thumbImg}
                          onError={e => { e.target.src = PLACEHOLDER; }} />
                        <div style={s.thumbHover}>
                          <i className="bi bi-folder2-open" style={{ fontSize:20,marginBottom:4 }} />
                          <span style={{ fontSize:12,fontWeight:600 }}>Open Project</span>
                        </div>
                      </div>

                      {/* Card body */}
                      <div style={{ padding:'14px 16px 0',flex:1,cursor:isConfirming?'default':'pointer' }}
                        onClick={() => !isConfirming && handleOpen(p.id)}>
                        <div style={{ display:'flex',alignItems:'flex-start',gap:10 }}>
                          <div style={{ width:34,height:34,borderRadius:8,background:'#f1f3f5',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:'1px solid #e9ecef' }}>
                            <span style={{ fontSize:'0.72rem',fontWeight:700,color:'#495057',letterSpacing:'-0.01em' }}>{getInitials(p.name)}</span>
                          </div>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={s.cardName}>{p.name || 'Untitled'}</div>
                            <div style={s.cardMeta}>
                              <i className="bi bi-clock" style={{ marginRight:4,fontSize:10 }} />
                              {fmtDate(p.updatedAt)}
                            </div>
                          </div>
                        </div>
                        {p.description && <p style={s.cardDesc}>{p.description}</p>}
                      </div>

                      {/* Actions */}
                      <div style={{ padding:'12px 16px' }}>
                        {!isConfirming ? (
                          <div style={{ display:'flex',gap:8 }}>
                            <button className="ob" onClick={() => handleOpen(p.id)} style={s.openBtn}>
                              <i className="bi bi-folder2-open" style={{ marginRight:6 }} />Open
                            </button>
                            <button className="db" onClick={() => setConfirmDeleteId(p.id)} style={s.deleteBtn} title="Delete">
                              <i className="bi bi-trash3" />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                            <span style={{ fontSize:12,color:'#dc2626',fontWeight:600 }}>
                              <i className="bi bi-exclamation-triangle-fill" style={{ marginRight:5 }} />Delete this project?
                            </span>
                            <div style={{ display:'flex',gap:8 }}>
                              <button onClick={() => handleDelete(p.id)} style={{ flex:1,background:'#dc2626',color:'#fff',border:'none',borderRadius:8,padding:'8px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:FF }}>
                                <i className="bi bi-check-lg" style={{ marginRight:5 }} />Yes, Delete
                              </button>
                              <button onClick={() => setConfirmDeleteId(null)} style={{ background:'#fff',color:'#495057',border:'1px solid #dee2e6',borderRadius:8,padding:'8px 14px',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:FF }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

              </div>

              {/* ── Pagination bar ──────────────────────────────────────── */}
              {lastPage > 1 && (
                <div style={s.pagination}>
                  {/* Info */}
                  <span style={s.pgInfo}>
                    Showing {metaFrom}–{metaTo} of {total} projects
                  </span>

                  {/* Controls */}
                  <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                    {/* Prev */}
                    <button className="pg-btn" disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)} style={s.pgBtn(currentPage === 1)}>
                      <i className="bi bi-chevron-left" />
                    </button>

                    {/* Page numbers */}
                    {pageButtons().map((pg, i) =>
                      pg === '...' ? (
                        <span key={`dots-${i}`} style={{ padding:'0 6px',color:'#adb5bd',fontSize:13 }}>…</span>
                      ) : (
                        <button key={pg} className={pg === currentPage ? '' : 'pg-btn'}
                          onClick={() => handlePageChange(pg)}
                          style={pg === currentPage ? s.pgBtnActive : s.pgBtn(false)}>
                          {pg}
                        </button>
                      )
                    )}

                    {/* Next */}
                    <button className="pg-btn" disabled={currentPage === lastPage}
                      onClick={() => handlePageChange(currentPage + 1)} style={s.pgBtn(currentPage === lastPage)}>
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>

                  {/* Per-page info */}
                  <span style={s.pgInfo}>{PER_PAGE} per page</span>
                </div>
              )}
            </>
          )}
        </>
      )}

      <SaveProjectModal
        open={showCreateModal}
        mode="create"
        loading={false}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
      />

      {/* Account sheet — mobile only. MobileSheetWrapper is a no-op
          passthrough on desktop, so without this outer isMobile guard
          AccountDetailsPanel would render unconditionally there too. */}
      {isMobile && (
        <MobileSheetWrapper isMobile={isMobile} open={mobileAccountOpen} onClose={() => setMobileAccountOpen(false)} title="Account">
          <div style={{ padding: '4px 4px 16px' }}>
            <AccountDetailsPanel />
          </div>
        </MobileSheetWrapper>
      )}
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────── */
const FF = "'Inter','Segoe UI',system-ui,-apple-system,sans-serif";

const s = {
  page: { minHeight:'100vh', background:'#f8f9fa', fontFamily:FF, padding:'32px 36px 60px', textAlign:'left' },

  header: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:14 },
  title:  { fontSize:22, fontWeight:700, color:'#212529', margin:0, letterSpacing:'-0.02em' },
  sub:    { fontSize:13, color:'#6c757d', margin:'4px 0 0' },

  primaryBtn: {
    display:'inline-flex', alignItems:'center', background:'#00A19D', color:'#fff', border:'none',
    padding:'10px 20px', borderRadius:9, fontSize:14, fontWeight:600, cursor:'pointer',
    boxShadow:'0 4px 14px rgba(0,161,157,0.28)', fontFamily:FF,
  },
  toolbar:     { display:'flex', gap:10, alignItems:'center', marginBottom:20 },
  searchInput: {
    width:'100%', padding:'10px 36px 10px 36px', boxSizing:'border-box',
    background:'#fff', border:'1.5px solid #dee2e6', borderRadius:9,
    color:'#212529', fontSize:14, fontFamily:FF, transition:'border-color 0.15s',
  },
  ghostBtn: {
    display:'inline-flex', alignItems:'center', background:'#fff', color:'#495057',
    border:'1.5px solid #dee2e6', padding:'9px 16px', borderRadius:9,
    fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:FF, whiteSpace:'nowrap',
  },

  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(290px,1fr))', gap:18 },

  card: {
    background:'#fff', border:'1px solid #e9ecef', borderRadius:14, overflow:'hidden',
    display:'flex', flexDirection:'column', boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
    transition:'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
  },
  thumb:     { position:'relative', height:160, background:'#f1f3f5', overflow:'hidden', cursor:'pointer', flexShrink:0 },
  thumbImg:  { width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.3s ease' },
  thumbHover:{
    position:'absolute', inset:0, background:'rgba(0,0,0,0.45)', color:'#fff',
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
    opacity:0, transition:'opacity 0.2s',
  },
  cardName: { fontSize:14, fontWeight:700, color:'#212529', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', letterSpacing:'-0.01em' },
  cardMeta: { fontSize:11, color:'#adb5bd', display:'flex', alignItems:'center', marginTop:3 },
  cardDesc: { fontSize:12, color:'#6c757d', margin:'10px 0 0', lineHeight:1.55, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' },
  openBtn:  { flex:1, display:'inline-flex', alignItems:'center', justifyContent:'center', background:'#00A19D', color:'#fff', border:'none', padding:'9px 12px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', transition:'background 0.15s', fontFamily:FF, boxShadow:'0 3px 10px rgba(0,161,157,0.25)' },
  deleteBtn:{ background:'#fff', color:'#adb5bd', border:'1.5px solid #dee2e6', padding:'9px 13px', borderRadius:8, fontSize:14, cursor:'pointer', transition:'all 0.15s', fontFamily:FF, display:'flex', alignItems:'center', justifyContent:'center' },
  newCard:  { background:'#fff', border:'1.5px dashed #dee2e6', borderRadius:14, display:'flex', flexDirection:'column', alignItems:'flex-start', justifyContent:'flex-end', minHeight:220, cursor:'pointer', transition:'border-color 0.18s', padding:'20px', gap:0 },

  // Pagination
  pagination: {
    display:'flex', alignItems:'center', justifyContent:'space-between',
    flexWrap:'wrap', gap:12, marginTop:28, padding:'14px 20px',
    background:'#fff', borderRadius:12, border:'1px solid #e9ecef',
    boxShadow:'0 1px 3px rgba(0,0,0,0.04)',
  },
  pgInfo: { fontSize:12, color:'#adb5bd', fontWeight:500, whiteSpace:'nowrap' },
  pgBtn: (disabled) => ({
    minWidth:36, height:36, padding:'0 10px', borderRadius:8,
    border:'1.5px solid #dee2e6', background: disabled ? '#f8f9fa' : '#fff',
    color: disabled ? '#ced4da' : '#495057',
    fontSize:13, fontWeight:500, cursor: disabled ? 'not-allowed' : 'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
    transition:'all 0.15s', fontFamily:FF,
  }),
  pgBtnActive: {
    minWidth:36, height:36, padding:'0 10px', borderRadius:8,
    border:'1.5px solid #00A19D', background:'#00A19D', color:'#fff',
    fontSize:13, fontWeight:700, cursor:'default',
    display:'flex', alignItems:'center', justifyContent:'center', fontFamily:FF,
  },
};

/* ── Mobile "Library" styles ────────────────────────────────────
   Modeled on the reference photo-library app screenshot: minimal top
   bar (small caps brand + big title + icon actions), a tight 2-col
   image-forward grid (rounded thumbnails with an overlaid status
   badge), name + timestamp below each thumbnail instead of inside a
   bordered card. */
const ms = {
  page: { minHeight:'100vh', background:'#ffffff', fontFamily:FF, padding:'0 0 32px', textAlign:'left' },

  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'18px 16px 14px' },
  brand:  { fontSize:10, fontWeight:700, letterSpacing:'0.12em', color:'#94A3AF', textTransform:'uppercase' },
  title:  { fontSize:24, fontWeight:800, color:'#1A1F36', margin:'3px 0 0', letterSpacing:'-0.02em' },

  iconBtn: {
    width:36, height:36, borderRadius:'50%', border:'1px solid #ECEEF2', background:'#fff',
    color:'#334155', display:'flex', alignItems:'center', justifyContent:'center',
    cursor:'pointer', fontSize:14, flexShrink:0,
  },
  iconBtnPrimary: {
    width:36, height:36, borderRadius:'50%', border:'none', background:'#00A19D',
    color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
    cursor:'pointer', fontSize:16, flexShrink:0, boxShadow:'0 3px 10px rgba(0,161,157,0.3)',
  },

  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px 12px', padding:'0 16px' },

  newTile: {
    // Fixed px height (not aspect-ratio) so every tile — this one and every
    // thumbBox below — is pixel-identical regardless of the source image's
    // own dimensions or aspect-ratio CSS support: aspect-ratio only
    // constrains the box, and on a webview where it's ignored the <img>'s
    // height:100% has nothing definite to resolve against, so it falls
    // back to each photo's own natural size — different photo, different
    // card height. A fixed height sidesteps that entirely.
    height:150, borderRadius:16, border:'1.5px dashed #D8DEE8', background:'#F8FAFC',
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
    gap:8, cursor:'pointer',
  },
  newTileIcon: {
    width:36, height:36, borderRadius:'50%', background:'#fff', border:'1px solid #E2E8F0',
    display:'flex', alignItems:'center', justifyContent:'center', color:'#00A19D', fontSize:15,
  },

  // Professional-card look: a real (bold) border + soft shadow around the
  // whole unit (image + name + date), not just a bare floating photo —
  // thumbBox sits as the card's first child so its top corners get clipped
  // round by the card's own overflow:hidden + borderRadius; it doesn't
  // need its own.
  card: {
    border:'2px solid #CBD5E1', borderRadius:16, background:'#ffffff', overflow:'hidden',
    boxShadow:'0 1px 4px rgba(15,23,60,0.06)', display:'flex', flexDirection:'column',
  },
  thumbBox: { position:'relative', height:150, background:'#F1F3F5', cursor:'pointer' },
  thumbImg: { width:'100%', height:'100%', objectFit:'cover', display:'block' },
  badge: {
    position:'absolute', top:8, left:8, background:'rgba(0,161,157,0.92)', color:'#fff',
    fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:999,
    letterSpacing:'0.04em', textTransform:'uppercase',
  },
  deleteDot: {
    position:'absolute', top:8, right:8, width:26, height:26, borderRadius:'50%',
    background:'rgba(15,23,42,0.55)', border:'none', color:'#fff',
    display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:11,
  },
  confirmOverlay: {
    position:'absolute', inset:0, background:'rgba(15,23,42,0.78)',
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
    gap:8, padding:10,
  },
  confirmYes: { background:'#dc2626', color:'#fff', border:'none', borderRadius:6, padding:'5px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:FF },
  confirmNo:  { background:'#fff', color:'#334155', border:'none', borderRadius:6, padding:'5px 10px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:FF },

  cardName: { fontSize:13, fontWeight:700, color:'#1A1F36', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', letterSpacing:'-0.01em', marginTop:2 },
  cardMeta: { fontSize:11, color:'#94A3AF', marginTop:2 },

  // Floating bottom navbar — fixed to the viewport, centered, pill-shaped,
  // matching the editor's MobileBottomToolbar look instead of an inline
  // bar in the page's own scroll flow.
  pagFloatWrap: {
    position:'fixed', left:0, right:0, bottom:0, zIndex:500,
    display:'flex', justifyContent:'center',
    padding:'0 16px calc(14px + env(safe-area-inset-bottom, 0px))',
    pointerEvents:'none',
  },
  pagination: {
    pointerEvents:'auto',
    display:'flex', alignItems:'center', gap:14,
    background:'rgba(255,255,255,0.95)', backdropFilter:'blur(10px)',
    borderRadius:999, border:'1px solid #ECEEF2',
    boxShadow:'0 8px 24px rgba(15,23,42,0.16)',
    padding:'8px 18px',
  },
  pgInfo: { fontSize:12, color:'#64748b', fontWeight:600, whiteSpace:'nowrap' },
  pgBtn: (disabled) => ({
    width:32, height:32, borderRadius:'50%', border:'1px solid #ECEEF2',
    background: disabled ? '#F8FAFC' : '#fff', color: disabled ? '#CBD5E1' : '#334155',
    fontSize:13, cursor: disabled ? 'not-allowed' : 'pointer',
    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
  }),
};
